import React, { ReactNode } from 'react'
import Select, { StylesConfig, MenuPlacement } from 'react-select'
import AsyncSelect from 'react-select/async'
import CreatableSelect from 'react-select/creatable'
import { useTranslation } from 'react-i18next'
import Label from '../formLabel'
export type Option = {
    label: string
    value: string | number | string[]
    [name: string]: any
}
interface OptionI {
    image: string
    label: string
    customAbbreviation: string
    value: string
}

interface Props {
    onCreateOption?: (str: string) => void
    async?: boolean
    label?: string
    required?: boolean
    placeholder?: string
    value?: Option | Option[] | null
    isClearable?: boolean
    isSearchable?: boolean
    onChange?: (data: any) => void
    options?: Option[]
    isMulti?: boolean
    defaultValue?: Option | null
    error?: string | string[]
    success?: boolean
    disabled?: boolean
    information?: string
    badge?: React.ReactNode
    asyncLoadOptions?: (value: string) => Promise<any[]>
    asyncFormatOptionLabel?: (option: any) => ReactNode
    formatOptionLabel?: (option: any) => ReactNode
    customStyleOption?: StylesConfig
    className?: string
    classNamePrefix?: 'testops' | 'testops-sm' | 'testops-lg' | 'select-tooltip'
    menuPlacement?: MenuPlacement
    components?: any
    max?: number
    testId?: string
    inputValue?: string
    menuPortalTarget?: HTMLElement | null
    highlight?: boolean
    onInputChange?: (newValue: string, actionMeta: any) => void
    isLoading?: boolean
    onMenuScrollToBottom?: () => void
    loadingMessage?: () => string
}

const Dropdown: React.FC<Props> = ({
    onCreateOption,
    async = false,
    label,
    required = false,
    isSearchable = true,
    placeholder,
    options = [],
    isClearable = false,
    value = [],
    onChange = () => {},
    isMulti = false,
    defaultValue = null,
    error,
    success = false,
    disabled = false,
    information,
    badge,
    asyncLoadOptions,
    asyncFormatOptionLabel,
    formatOptionLabel,
    customStyleOption = {},
    className = '',
    classNamePrefix = 'testops',
    menuPlacement = 'bottom',
    components = {},
    max = 1000,
    testId = '',
    inputValue,
    menuPortalTarget = null,
    highlight = false,
    onInputChange = (newValue: string, actionMeta: any) => {},
    isLoading = false,
    onMenuScrollToBottom = () => {},
    loadingMessage = () => 'Loading...',
}) => {
    const { t } = useTranslation()
    const resolvedPlaceholder = placeholder ?? t('Select')
    const resolvedNoOptionsMessage = () => t('No options')

    const getBackgroundColor = (isDisabled: boolean, isFocused: boolean, isSelected: boolean) => {
        if (isDisabled) return '#FCFCFC'
        if (isFocused) return '#f5e00361'
        if (isSelected) return '#f5e003'
        return '#ffffff'
    }

    const defaultStyleOption: StylesConfig<Option, boolean> = {
        control: (styles) => ({
            ...styles,
            border: '1px solid #d5dad4',
            minHeight: classNamePrefix == 'testops-lg' ? '2.5rem' : '1.709rem',
            boxShadow: 'none',
            cursor: 'pointer',
            ':hover': {
                border: '1px solid #d5dad4',
                boxShadow: 'none',
            },
            ':focus': {
                border: '1px solid #d5dad4',
                outline: 'none',
                boxShadow: 'none',
            },
        }),
        placeholder: (styles) => ({
            ...styles,
            fontSize: classNamePrefix == 'testops-lg' ? '0.7rem' : '0.583rem',
            lineHeight: 1,
        }),
        singleValue: (styles) => ({
            ...styles,
            fontSize: '0.583rem',
            lineHeight: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
        }),
        input: (styles) => ({ ...styles, lineHeight: 1, fontSize: '0.583rem' }),
        option: (styles, { data, isDisabled, isFocused, isSelected }) => {
            return {
                ...styles,
                backgroundColor: getBackgroundColor(isDisabled, isFocused, isSelected),
                color: '#000000',
                cursor: isDisabled ? 'not-allowed' : 'default',
                border: '1px solid #f4f4f4',
                fontSize: '0.583rem',
                padding: classNamePrefix == 'testops-lg' ? '0.25rem 0.5rem' : '0 0.5rem',
            }
        },
        menuList: (styles, props) => ({
            ...styles,
            padding: 0,
        }),
        multiValue: (base) => ({
            ...base,
            borderRadius: '1.667rem',
            backgroundColor: '#EFEFEF',
        }),
        valueContainer: (base) => ({
            ...base,
            maxHeight: '5.125rem',
            overflowY: 'scroll',
        }),
        multiValueLabel: (base) => ({
            ...base,
            fontSize: '0.458rem',
            lineHeight: '0.458rem',
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: 'black',
            cursor: 'pointer',
            ':hover': {
                color: 'red',
            },
        }),
        indicatorsContainer: (style) => ({
            ...style,
        }),

        dropdownIndicator: (provided) => ({
            ...provided,
            padding: '0 0.25rem',
        }),
        clearIndicator: (provided) => ({
            ...provided,
            padding: '0 0.25rem',
        }),
        indicatorSeparator: () => ({
            display: 'none', // Hide the separator
        }),
        noOptionsMessage: (styles) => ({
            ...styles,
            fontSize: '0.583rem',
            padding: 0,
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 99999999, // Higher than other elements
        }),
    }

    const labelId = `${label?.toLowerCase().replaceAll(/\s+/g, '-')}-label-${React.useId()}`

    const isOptionDisabled = () => {
        if (isMulti && value) {
            return value.length >= max
        }
        return false
    }

    const getStatusClass = () => {
        if (error) {
            return 'error '
        }
        if (highlight) {
            return 'highlight'
        }
        if (success) {
            return 'success '
        }
        return ''
    }

    return (
        <div className={className}>
            {label && <Label text={label} required={required} information={information} badge={badge} htmlFor={labelId} />}
            {async && (
                <AsyncSelect
                    menuPortalTarget={menuPortalTarget}
                    inputId={label ? labelId : (testId ?? undefined)}
                    data-testid={label ? labelId : (testId ?? undefined)}
                    aria-labelledby={label ? labelId : (testId ?? undefined)}
                    classNamePrefix={`${getStatusClass()} ${classNamePrefix}`}
                    placeholder={resolvedPlaceholder}
                    isClearable={isClearable}
                    isMulti={isMulti}
                    isSearchable={isSearchable}
                    defaultOptions
                    loadOptions={asyncLoadOptions}
                    formatOptionLabel={asyncFormatOptionLabel}
                    styles={
                        {
                            ...defaultStyleOption,
                            ...customStyleOption,
                        } as StylesConfig<Option, boolean>
                    }
                    value={value}
                    onChange={onChange}
                    menuPlacement={menuPlacement}
                    isOptionDisabled={isOptionDisabled}
                    noOptionsMessage={resolvedNoOptionsMessage}
                />
            )}

            {onCreateOption && (
                <CreatableSelect
                    menuPortalTarget={menuPortalTarget}
                    inputId={label ? labelId : (testId ?? undefined)}
                    data-testid={label ? labelId : (testId ?? undefined)}
                    aria-labelledby={label ? labelId : (testId ?? undefined)}
                    classNamePrefix={error ? `error ${classNamePrefix}` : classNamePrefix}
                    placeholder={resolvedPlaceholder}
                    isDisabled={disabled}
                    isClearable={isClearable}
                    isMulti={isMulti}
                    isSearchable={isSearchable}
                    defaultValue={defaultValue}
                    closeMenuOnSelect={!isMulti}
                    options={options}
                    onChange={onChange}
                    value={value}
                    inputValue={inputValue}
                    onCreateOption={onCreateOption}
                    formatOptionLabel={formatOptionLabel}
                    styles={
                        {
                            ...defaultStyleOption,
                            ...customStyleOption,
                        } as StylesConfig<Option, boolean>
                    }
                    menuPlacement={menuPlacement}
                    isOptionDisabled={isOptionDisabled}
                    onInputChange={onInputChange}
                    noOptionsMessage={resolvedNoOptionsMessage}
                />
            )}

            {!onCreateOption && !async && (
                <Select
                    menuPortalTarget={menuPortalTarget}
                    inputId={label ? labelId : (testId ?? undefined)}
                    data-testid={label ? labelId : (testId ?? undefined)}
                    aria-labelledby={label ? labelId : (testId ?? undefined)}
                    classNamePrefix={`${getStatusClass()} ${classNamePrefix}`}
                    placeholder={resolvedPlaceholder}
                    isDisabled={disabled}
                    isClearable={isClearable}
                    isSearchable={isSearchable}
                    isMulti={isMulti}
                    defaultValue={defaultValue}
                    closeMenuOnSelect={!isMulti}
                    options={options}
                    onChange={onChange}
                    value={value}
                    formatOptionLabel={formatOptionLabel}
                    styles={
                        {
                            ...defaultStyleOption,
                            ...customStyleOption,
                        } as StylesConfig<Option, boolean>
                    }
                    menuPlacement={menuPlacement}
                    menuPosition="absolute"
                    components={components}
                    isOptionDisabled={isOptionDisabled}
                    isLoading={isLoading}
                    onMenuScrollToBottom={onMenuScrollToBottom}
                    loadingMessage={loadingMessage}
                    noOptionsMessage={resolvedNoOptionsMessage}
                />
            )}

            {error && <p className="text-error text-[.542rem] leading-[1.2] mt-1">{typeof error === 'string' ? error : error?.join(', ')}</p>}
        </div>
    )
}

export default Dropdown
